import React, { forwardRef, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTime, Info, Settings } from 'luxon';
import DatePicker, { DatePickerType } from '../DatePicker';
import { IconType, Icon, Theme } from '@routific/hive';
import { DateValidationError } from '@mui/x-date-pickers';
import useDateRangePicker from './hooks/useDateRangePicker';

export type DateRangeValidationError = DateValidationError | 'maxRange' | 'startAfterEnd' | 'default';

interface DateRangePickerProps {
  value: [DateTime | null, DateTime | null];
  onChange: (value: [string | undefined, string | undefined]) => void;
  onError: (err: DateRangeValidationError) => void;
  dateRangeLimit: number;
  queryFn?: (startDate: string | undefined, endDate: string | undefined) => Promise<any>,
  dayHasRoutes?: (dayToCheck: any) => boolean,
  defaultValue?: [DateTime | null, DateTime | null];
}

const DateRangePicker: React.FC<DateRangePickerProps> = forwardRef(({ onChange, onError, dateRangeLimit, queryFn, dayHasRoutes, defaultValue }, ref) => {
  const [error, setError] = useState<DateRangeValidationError | null>(null);

  const {
    startDate,
    endDate,
    openStartPicker,
    openEndPicker,
    setOpenEndPicker,
    setOpenStartPicker,
    onOpenStart,
    onOpenEnd,
    onMonthChange,
    onYearChange,
    handleStartAccepted,
    handleStartDateChange,
    handleEndDateChange,
  } = useDateRangePicker(onChange, defaultValue ?? [null, null], queryFn);

  useEffect(() => {
    if (startDate && endDate) {
      setError(startDate > endDate ? 'startAfterEnd' : null);
    }
    if (endDate && startDate) {
      const monthsDifference = endDate.diff(startDate, 'months').months;
      if (monthsDifference >= dateRangeLimit) {
        setError('maxRange');
      }
    }
  }, [startDate, endDate, setError]);

  useEffect(() => {
    onError(error);
  }, [error, onError]);

  const handleError = (err: DateRangeValidationError) => {
    try {
      setError(err);
    } catch {
      setError('default');
    }
  };

  try {
    Settings.defaultWeekSettings = {
      // Sunday = 7, Monday = 1.
      firstDay: 7,
      minimalDays: Info.getMinimumDaysInFirstWeek(),
      weekend: Info.getWeekendWeekdays(),
    };
  } catch (e) {
    console.error('Error setting Luxon default week settings inside DatePickerRange:', e);
  }

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Box display="flex" alignItems="centre" width="100%" gap={2} ref={ref}>
        <DatePicker
          type={DatePickerType.START}
          label="Start date"
          value={startDate}
          onChange={handleStartDateChange}
          onOpen={onOpenStart}
          onClose={() => setOpenStartPicker(false)}
          onAccept={handleStartAccepted}
          open={openStartPicker}
          required
          onJumpToToday={() => handleStartDateChange(DateTime.now())}
          rangeStart={startDate}
          rangeEnd={endDate}
          onMonthChange={onMonthChange}
          onYearChange={onYearChange}
          onError={handleError}
          hasError={!!error}
          dayHasRoutes={dayHasRoutes}
        />
        <Box display="flex" alignItems="center" height={40} paddingTop={Theme.Spacing.xlSpacing}>
          <Icon icon={IconType.Forward} type="secondary" />
        </Box>
        <DatePicker
          type={DatePickerType.END}
          label="End date"
          value={endDate}
          onChange={handleEndDateChange}
          onOpen={onOpenEnd}
          onClose={() => setOpenEndPicker(false)}
          open={openEndPicker}
          required
          onJumpToToday={() => handleEndDateChange(DateTime.now())}
          rangeStart={startDate}
          rangeEnd={endDate}
          onMonthChange={onMonthChange}
          onYearChange={onYearChange}
          onError={handleError}
          hasError={!!error}
          dayHasRoutes={dayHasRoutes}
        />
      </Box>
    </LocalizationProvider>
  );
});

export default DateRangePicker;
